import { Button } from '@ads/components-react';
import {
  ArrowLongRightIcon,
  ArrowPathIcon,
  MapPinIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import type { EnrollmentHistory, PoloChangeHistory } from '../../../types/enrollment';
import { buildEnrollmentHistoryCsv } from '../../../utils/buildEnrollmentHistoryCsv';
import { EnrollmentStatusBadge } from '../../atoms/EnrollmentStatusBadge';

// ─── Props ────────────────────────────────────────────────────────────────────

/**
 * Aba de histórico de status e polos da matrícula.
 *
 * @param history - Lista de transições de status da matrícula
 * @param poloChanges - Lista de mudanças de polo
 * @param poleName - Nome do polo atual
 * @param poleId - ID do polo atual (null = sem vínculo presencial)
 * @param enrollmentDate - Data de matrícula (fallback para "vinculada desde")
 */
export interface EnrollmentHistoryTabProps {
  readonly history: readonly EnrollmentHistory[];
  readonly poloChanges: readonly PoloChangeHistory[];
  readonly poleName: string | null;
  readonly poleId: number | null;
  readonly enrollmentDate: string;
}

// ─── Local sub-components ────────────────────────────────────────────────────

function SectionCard({
  children,
  header,
}: {
  children: React.ReactNode;
  header?: React.ReactNode;
}) {
  return (
    <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface overflow-hidden">
      {header && (
        <div className="border-b-hairline border-ctx-layout-border px-xxs py-quark">{header}</div>
      )}
      <div className="px-xxs py-xxxs">{children}</div>
    </div>
  );
}

function SectionTitle({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-nano">
      <div className="flex items-center gap-mini">
        {Icon && <Icon className="h-xxxs w-xxxs text-ctx-content-placeholder" />}
        <div className="flex flex-col">
          <span className="ts-paragraph-xs !font-medium text-ctx-content-title">{title}</span>
          {description && (
            <span className="ts-caption-xxs text-ctx-content-placeholder">{description}</span>
          )}
        </div>
      </div>
      {action}
    </div>
  );
}

function InfoField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-quark">
      <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
        {label}
      </span>
      <span className="ts-paragraph-xs !font-medium text-ctx-content-title">{value ?? '—'}</span>
    </div>
  );
}

// ─── Timeline Dot ─────────────────────────────────────────────────────────────

type DotVariant = 'status' | 'create' | 'polo';

function TimelineDot({ variant }: { variant: DotVariant }) {
  const dotColor: Record<DotVariant, string> = {
    status: 'bg-ctx-interactive-primary',
    create: 'bg-base-success-500',
    polo: 'bg-base-warning-500',
  };
  return (
    <div className="flex h-[1.25rem] w-[1.25rem] shrink-0 items-center justify-center rounded-circular border-hairline border-ctx-layout-border bg-ctx-layout-surface">
      <div className={`h-[0.375rem] w-[0.375rem] rounded-circular ${dotColor[variant]}`} />
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function EnrollmentHistoryTab({
  history,
  poloChanges,
  poleName,
  poleId,
  enrollmentDate,
}: EnrollmentHistoryTabProps) {
  const { t } = useTranslation('personManagement');

  const sortedHistory = [...history].sort(
    (a, b) => new Date(b.creationDate ?? '').getTime() - new Date(a.creationDate ?? '').getTime(),
  );

  const sortedPoloChanges = [...poloChanges].sort(
    (a, b) => new Date(b.dateLog ?? '').getTime() - new Date(a.dateLog ?? '').getTime(),
  );

  const mostRecentPoloChange = sortedPoloChanges[0];
  const linkedSince = mostRecentPoloChange?.dateLog ?? enrollmentDate;
  const poloChangeCount = sortedPoloChanges.filter(pc => pc.oldPoloName !== null).length;

  const handleExportHistory = () => {
    const csv = buildEnrollmentHistoryCsv(
      sortedHistory,
      {
        dateTime: t('enrollmentHistory.csv.dateTime'),
        previousStatus: t('enrollmentHistory.csv.previousStatus'),
        newStatus: t('enrollmentHistory.csv.newStatus'),
        reason: t('enrollmentHistory.motivo'),
        subReason: t('enrollmentHistory.subMotivo'),
        observation: t('enrollmentHistory.csv.observation'),
        responsible: t('enrollmentHistory.csv.responsible'),
      },
      status => t(`enrollmentStatus.${status}`),
    );

    // BOM (﻿) garante que o Excel reconheça UTF-8 e acentos corretamente.
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = t('enrollmentHistory.csv.fileName');
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-xxs bg-ctx-layout-body py-xs">
      {/* ── Status Timeline ── */}
      <SectionCard
        header={
          <SectionTitle
            icon={ArrowPathIcon}
            title={t('enrollmentHistory.statusTimelineTitle')}
            description={t('enrollmentHistory.statusTimelineDescription')}
            action={
              <div className="flex items-center gap-xxs">
                <span className="ts-caption-xxs text-ctx-content-placeholder hidden sm:inline">
                  {t('enrollmentHistory.periodAll')}
                </span>
                <Button
                  hierarchy="tertiary"
                  size="sm"
                  onClick={handleExportHistory}
                  disabled={sortedHistory.length === 0}
                >
                  {t('enrollmentHistory.exportHistory')}
                </Button>
              </div>
            }
          />
        }
      >
        {sortedHistory.length === 0 ? (
          <p className="ts-paragraph-xs text-ctx-content-placeholder py-xxs">
            {t('enrollmentHistory.noStatusHistory')}
          </p>
        ) : (
          <div className="flex flex-col pt-quark">
            {sortedHistory.map((entry, index) => (
              <div key={entry.id} className="flex gap-xxs">
                {/* Left column: dot + connector line */}
                <div className="flex flex-col items-center">
                  <TimelineDot variant={entry.oldStatus === null ? 'create' : 'status'} />
                  {index < sortedHistory.length - 1 && (
                    <div className="w-[1px] flex-1 min-h-[1.5rem] bg-ctx-layout-border mt-quark" />
                  )}
                </div>

                {/* Entry content */}
                <div className="flex flex-col gap-quark pb-xs flex-1 min-w-none">
                  {/* Status transition + datetime */}
                  <div className="flex flex-wrap items-start justify-between gap-xs">
                    <div className="flex flex-wrap items-center gap-quark">
                      {entry.oldStatus !== null ? (
                        <>
                          <EnrollmentStatusBadge status={entry.oldStatus} />
                          <ArrowLongRightIcon className="h-xxxs w-xxxs shrink-0 text-ctx-content-placeholder" />
                          <EnrollmentStatusBadge status={entry.newStatus} />
                        </>
                      ) : (
                        <div className="flex items-center gap-quark">
                          <PlusIcon className="h-xxxs w-xxxs shrink-0 text-base-success-500" />
                          <EnrollmentStatusBadge status={entry.newStatus} />
                        </div>
                      )}
                    </div>
                    <span className="ts-caption-xxs text-ctx-content-placeholder shrink-0 whitespace-nowrap tabular-nums">
                      {formatDateTime(entry.creationDate ?? '')}
                    </span>
                  </div>

                  {/* Reason + sub-reason */}
                  {entry.reasonName && (
                    <p className="ts-caption-xxs text-ctx-content-base">
                      <span className="text-ctx-content-placeholder">
                        {t('enrollmentHistory.motivo')}:{' '}
                      </span>
                      {entry.reasonName}
                      {entry.subReasonName && (
                        <>
                          <span className="text-ctx-content-placeholder">
                            {' '}
                            · {t('enrollmentHistory.subMotivo')}:{' '}
                          </span>
                          {entry.subReasonName}
                        </>
                      )}
                    </p>
                  )}

                  {/* Observation */}
                  {entry.observation && (
                    <div className="py-nano px-mini bg-ctx-layout-body rounded-sm">
                      <p className="ts-caption-xxs text-ctx-content-base italic">
                        {entry.observation}
                      </p>
                    </div>
                  )}

                  {/* Attribution */}
                  <p className="ts-caption-xxs text-ctx-content-placeholder">
                    {t('enrollmentHistory.by')} {entry.creationUser}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* ── Polo History ── */}
      <SectionCard
        header={
          <SectionTitle
            icon={MapPinIcon}
            title={t('enrollmentHistory.poloHistoryTitle')}
            description={t('enrollmentHistory.poloHistoryDescription')}
          />
        }
      >
        {/* Current polo summary */}
        <div className="mb-xxs rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body p-xxs">
          <div className="grid grid-cols-2 gap-xxxs md:grid-cols-3">
            <div className="flex flex-col gap-quark">
              <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
                {t('enrollmentHistory.currentPolo')}
              </span>
              <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
                {poleName ?? '—'}
              </span>
              {!poleId && (
                <span className="ts-caption-xxs text-ctx-content-placeholder">
                  {t('enrollmentHistory.noPresentialLink')}
                </span>
              )}
            </div>
            <InfoField label={t('enrollmentHistory.linkedSince')} value={formatDate(linkedSince)} />
            <InfoField
              label={t('enrollmentHistory.totalChanges')}
              value={
                poloChangeCount === 0
                  ? t('enrollmentHistory.noPoloChanges')
                  : t('enrollmentHistory.poloChanges', {
                      count: poloChangeCount,
                    })
              }
            />
          </div>
        </div>

        {/* Polo timeline */}
        {sortedPoloChanges.length === 0 ? (
          <p className="ts-paragraph-xs text-ctx-content-placeholder py-xxs">
            {t('enrollmentHistory.noPoloHistory')}
          </p>
        ) : (
          <div className="flex flex-col pt-quark">
            {sortedPoloChanges.map((entry, index) => (
              <div key={index} className="flex gap-xxs">
                {/* Dot + connector line */}
                <div className="flex flex-col items-center">
                  <TimelineDot variant="polo" />
                  {index < sortedPoloChanges.length - 1 && (
                    <div className="w-[1px] flex-1 min-h-[1.5rem] bg-ctx-layout-border mt-quark" />
                  )}
                </div>

                {/* Entry content */}
                <div className="flex flex-col gap-quark pb-xs flex-1 min-w-none">
                  <div className="flex flex-wrap items-start justify-between gap-xs">
                    <div className="flex items-center gap-quark">
                      {entry.oldPoloName !== null ? (
                        <>
                          <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
                            {entry.oldPoloName}
                          </span>
                          <ArrowLongRightIcon className="h-xxxs w-xxxs shrink-0 text-ctx-content-placeholder" />
                        </>
                      ) : (
                        <ArrowLongRightIcon className="h-xxxs w-xxxs shrink-0 text-ctx-content-placeholder" />
                      )}
                      <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
                        {entry.newPoloName}
                      </span>
                    </div>
                    <span className="ts-caption-xxs text-ctx-content-placeholder shrink-0 whitespace-nowrap tabular-nums">
                      {formatDateTime(entry.dateLog ?? '')}
                    </span>
                  </div>

                  <p className="ts-caption-xxs text-ctx-content-placeholder">
                    {entry.oldPoloName === null
                      ? t('enrollmentHistory.initialLinkLabel')
                      : t('enrollmentHistory.poloChangeLabel')}{' '}
                    · {t('enrollmentHistory.by')} {entry.executedBy}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
